import { Icon } from "@caint/design-system";
import type { PapelKey, UsuarioPublico } from "@caint/shared";
import { gerarSenhaProvisoria } from "@caint/shared";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { CredencialPronta } from "../../components/CredencialPronta.tsx";
import { UsuarioCampos } from "../../components/usuarios/UsuarioCampos.tsx";
import { Avatar, Destaque, EixoChip, PerfilBadge, StatusPill } from "../../components/usuarios/atomos.tsx";
import { nomeUnidade } from "../../lib/ciclos.ts";
import { mensagemAmigavel } from "../../lib/erros.ts";
import { nomeCargo } from "../../lib/planoUnidade.ts";
import { useUsuarioForm } from "../../lib/useUsuarioForm.ts";
import {
  type ColunaOrdem,
  type ContagemUsuarios,
  EIXOS,
  FILTROS_VAZIOS,
  type FiltrosUsuarios,
  GRUPO_ROTULO,
  type GrupoPapel,
  ORDEM_PADRAO,
  type Ordem,
  PAPEIS_ORDEM,
  PAPEL_GRUPO,
  type Pagina,
  type StatusFiltro,
  alternarOrdem,
  contarFiltrosAtivos,
  contarUsuarios,
  criadaEm,
  filtrarUsuarios,
  ordenarUsuarios,
  paginar,
  rotuloPapel,
  statusUsuario,
  temFiltro,
} from "../../lib/usuariosLista.ts";
import type { UsuariosApi } from "../../state/useAppState.ts";
import { Modal, PageHeader } from "../ui/kit.tsx";
import "./usuarios.css";

export interface UsuariosDesktopProps {
  papel: PapelKey;
  api: UsuariosApi;
  /** Id do gestor logado: marca a própria conta e impede que ele se desative. */
  euId?: string;
}

const GESTORES = new Set<PapelKey>(["admin", "coord"]);
const TAMANHOS_PAGINA = [25, 50, 100];
const GRUPOS: readonly GrupoPapel[] = ["avaliacao", "supervisao", "gestao", "leitura", "unidade"];
/** A busca também acha por unidade e cargo — a lib recebe os resolvedores. */
const OPCOES_BUSCA = { nomeUnidade: (id: string) => nomeUnidade(id), nomeCargo };

type Dialogo =
  | { tipo: "novo" }
  | { tipo: "editar"; usuario: UsuarioPublico }
  | { tipo: "senha"; usuario: UsuarioPublico }
  | { tipo: "mensagem"; usuario: UsuarioPublico }
  | { tipo: "desativar"; usuario: UsuarioPublico };

/** Fecha um popover ao clicar fora dele. */
function useFecharFora(ref: React.RefObject<HTMLElement | null>, aberto: boolean, fechar: () => void) {
  useEffect(() => {
    if (!aberto) {
      return;
    }
    const fora = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        fechar();
      }
    };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, [ref, aberto, fechar]);
}

export function UsuariosDesktop({ papel, api, euId }: UsuariosDesktopProps) {
  const [lista, setLista] = useState<UsuarioPublico[] | null>(null);
  /** Falha ao CARREGAR a lista (tela vira estado de erro com "tentar de novo"). */
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  /** Falha de uma AÇÃO sobre a lista já carregada (faixa dispensável). */
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ id: number; texto: string } | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo | null>(null);
  const [filtros, setFiltros] = useState<FiltrosUsuarios>(FILTROS_VAZIOS);
  const [ordem, setOrdem] = useState<Ordem>(ORDEM_PADRAO);
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(TAMANHOS_PAGINA[0] ?? 25);
  const busca = useRef<HTMLInputElement>(null);
  const cartao = useRef<HTMLDivElement>(null);
  const podeGerir = GESTORES.has(papel);

  const jaCarregou = useRef(false);
  const carregar = useCallback(async () => {
    setErroCarga(null);
    try {
      setLista(await api.listar());
      jaCarregou.current = true;
    } catch (e) {
      // Recarga que falha não apaga a lista que já estava na tela: vira só um
      // aviso. Antes qualquer falha trocava a tabela por "Sem usuários."
      if (jaCarregou.current) {
        setErro(mensagemAmigavel(e));
      } else {
        setErroCarga(mensagemAmigavel(e));
      }
    }
  }, [api]);

  useEffect(() => {
    if (podeGerir) {
      void carregar();
    }
  }, [podeGerir, carregar]);

  const avisar = useCallback((texto: string) => setAviso({ id: Date.now(), texto }), []);
  useEffect(() => {
    if (!aviso) {
      return;
    }
    const t = window.setTimeout(() => setAviso(null), 4500);
    return () => window.clearTimeout(t);
  }, [aviso]);

  // "/" leva o teclado à busca (padrão de ferramentas de gestão), sem roubar a
  // tecla de quem está digitando em outro campo ou com um diálogo aberto.
  const temDialogo = dialogo !== null;
  useEffect(() => {
    if (!podeGerir || temDialogo) {
      return;
    }
    const atalho = (e: KeyboardEvent) => {
      const alvo = e.target as HTMLElement | null;
      const digitando = alvo?.closest("input, textarea, select, [contenteditable='true']");
      if (e.key === "/" && !digitando && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        busca.current?.focus();
        busca.current?.select();
      }
    };
    document.addEventListener("keydown", atalho);
    return () => document.removeEventListener("keydown", atalho);
  }, [podeGerir, temDialogo]);

  /** Todo filtro novo volta à página 1 — senão a tela fica numa página que sumiu. */
  const filtrar = useCallback((parcial: Partial<FiltrosUsuarios>) => {
    setFiltros((f) => ({ ...f, ...parcial }));
    setPagina(1);
  }, []);
  const limparFiltros = useCallback(() => {
    setFiltros(FILTROS_VAZIOS);
    setPagina(1);
  }, []);
  const ordenarPor = useCallback((coluna: ColunaOrdem) => {
    setOrdem((o) => alternarOrdem(o, coluna));
    setPagina(1);
  }, []);

  const contagem = useMemo(() => contarUsuarios(lista ?? []), [lista]);
  const visiveis = useMemo(
    () => ordenarUsuarios(filtrarUsuarios(lista ?? [], filtros, OPCOES_BUSCA), ordem),
    [lista, filtros, ordem],
  );
  const pg = useMemo(() => paginar(visiveis, pagina, tamanho), [visiveis, pagina, tamanho]);

  const alternarAtivo = useCallback(
    async (u: UsuarioPublico, ativo: boolean) => {
      setErro(null);
      await api.atualizar(u.id, { ativo });
      avisar(ativo ? `Conta de ${u.nome} reativada.` : `Conta de ${u.nome} desativada.`);
      await carregar();
    },
    [api, avisar, carregar],
  );

  const reativar = useCallback(
    async (u: UsuarioPublico) => {
      try {
        await alternarAtivo(u, true);
      } catch (e) {
        setErro(mensagemAmigavel(e));
      }
    },
    [alternarAtivo],
  );

  /**
   * Recarrega a lista SEM fechar o modal. Criar usuário e redefinir senha
   * terminam mostrando a credencial — a senha só existe em claro naquele
   * instante, então fechar ali obrigaria a redefinir de novo. Quem fecha é o
   * "Concluir", depois de a pessoa copiar a mensagem.
   */
  const aposAcaoSemFechar = useCallback(
    (texto: string) => {
      avisar(texto);
      void carregar();
    },
    [avisar, carregar],
  );

  const fechar = useCallback(() => setDialogo(null), []);
  /**
   * Fecha SÓ se o diálogo aberto ainda for aquele. A resposta de uma ação pode
   * chegar depois de o gestor já ter aberto outro diálogo — e um `setDialogo(null)`
   * cego fecharia o diálogo errado, levando junto o que estava sendo digitado.
   */
  const fecharEste = useCallback((este: Dialogo) => setDialogo((d) => (d === este ? null : d)), []);

  if (!podeGerir) {
    return (
      <div className="dk-page">
        <PageHeader titulo="Usuários & Perfis" />
        <p className="spec">Acesso restrito à Coordenação e à Administração.</p>
      </div>
    );
  }

  const carregando = lista === null && !erroCarga;

  return (
    <div className="dk-page us-page">
      <PageHeader
        titulo="Usuários & Perfis"
        sub={lista ? resumoContas(contagem) : "Gestão de acesso"}
        acoes={
          <button
            type="button"
            className="m-btn primary us-novo"
            onClick={() => setDialogo({ tipo: "novo" })}
          >
            <Icon name="userPlus" size={16} />
            Novo usuário
          </button>
        }
      />

      {erro && (
        <div className="us-erro" role="alert">
          <Icon name="alert" size={16} />
          <span>{erro}</span>
          <button
            type="button"
            className="us-btn-icone"
            aria-label="Dispensar aviso"
            onClick={() => setErro(null)}
          >
            <Icon name="x" size={15} />
          </button>
        </div>
      )}

      <AbasStatus
        status={filtros.status}
        contagem={lista ? contagem : null}
        onMudar={(status) => filtrar({ status })}
      />

      <div className="us-card" ref={cartao}>
        <BarraFiltros
          filtros={filtros}
          contagem={contagem}
          refBusca={busca}
          onFiltrar={filtrar}
          onLimpar={limparFiltros}
        />

        {erroCarga ? (
          <Estado
            icone="alert"
            titulo="Não foi possível carregar os usuários"
            texto={erroCarga}
            acao={
              <button type="button" className="m-btn" onClick={() => void carregar()}>
                <Icon name="refresh" size={15} />
                Tentar novamente
              </button>
            }
          />
        ) : (
          <TabelaUsuarios
            linhas={pg.itens}
            carregando={carregando}
            busca={filtros.busca}
            ordem={ordem}
            euId={euId}
            onOrdenar={ordenarPor}
            onAbrir={setDialogo}
            onReativar={(u) => void reativar(u)}
            vazio={
              contagem.total === 0 ? (
                <Estado
                  icone="people"
                  titulo="Nenhum usuário cadastrado"
                  texto="Crie a primeira conta para dar acesso ao sistema."
                  acao={
                    <button
                      type="button"
                      className="m-btn primary"
                      onClick={() => setDialogo({ tipo: "novo" })}
                    >
                      Novo usuário
                    </button>
                  }
                />
              ) : (
                <Estado
                  icone="search"
                  titulo="Nenhum usuário encontrado"
                  texto={
                    filtros.busca.trim()
                      ? `Nada corresponde a “${filtros.busca.trim()}” com os filtros atuais.`
                      : "Nenhuma conta corresponde aos filtros atuais."
                  }
                  acao={
                    <button type="button" className="m-btn" onClick={limparFiltros}>
                      Limpar filtros
                    </button>
                  }
                />
              )
            }
          />
        )}

        {lista && pg.total > 0 && (
          <Rodape
            pg={pg}
            totalGeral={contagem.total}
            tamanho={tamanho}
            onPagina={(p) => {
              setPagina(p);
              // O paginador fica no fim da lista: a página nova começa do topo,
              // não de onde a rolagem estava.
              cartao.current?.scrollIntoView?.({ block: "start" });
            }}
            onTamanho={(t) => {
              setTamanho(t);
              setPagina(1);
            }}
          />
        )}
      </div>

      {/* A região viva fica SEMPRE montada: leitor de tela anuncia o que muda
          dentro dela, não um elemento que já nasce com o texto. */}
      <div className="us-toast-area" aria-live="polite">
        {aviso && (
          <div className="us-toast" key={aviso.id}>
            <Icon name="checkCircle" size={16} />
            <span>{aviso.texto}</span>
            <button type="button" aria-label="Fechar aviso" onClick={() => setAviso(null)}>
              <Icon name="x" size={14} />
            </button>
          </div>
        )}
      </div>

      {dialogo?.tipo === "novo" && (
        <FormModal
          api={api}
          onClose={fechar}
          onSalvo={(nome) => aposAcaoSemFechar(`Usuário ${nome} criado.`)}
        />
      )}
      {dialogo?.tipo === "editar" && (
        <FormModal
          key={dialogo.usuario.id}
          api={api}
          editando={dialogo.usuario}
          euId={euId}
          onClose={fechar}
          onSalvo={(nome) => {
            fecharEste(dialogo);
            aposAcaoSemFechar(`Dados de ${nome} atualizados.`);
          }}
        />
      )}
      {dialogo?.tipo === "mensagem" && (
        <MensagemModal
          usuario={dialogo.usuario}
          api={api}
          onClose={fechar}
          onNovaSenha={() => setDialogo({ tipo: "senha", usuario: dialogo.usuario })}
        />
      )}
      {dialogo?.tipo === "senha" && (
        <SenhaModal
          usuario={dialogo.usuario}
          api={api}
          onClose={fechar}
          onPronto={() => aposAcaoSemFechar("Senha redefinida.")}
        />
      )}
      {dialogo?.tipo === "desativar" && (
        <DesativarModal
          usuario={dialogo.usuario}
          onClose={fechar}
          onConfirmar={async () => {
            await alternarAtivo(dialogo.usuario, false);
            fecharEste(dialogo);
          }}
        />
      )}
    </div>
  );
}

/**
 * Unidades do filtro: só as que têm contas — mais a que está SELECIONADA, mesmo
 * que tenha ficado sem nenhuma (a conta foi movida). Sem isso o select voltava a
 * mostrar "Todas as unidades" com o filtro ainda ligado, e a lista ficava vazia
 * sem explicação.
 */
function unidadesDoFiltro(contagem: ContagemUsuarios, selecionada: string) {
  const porUnidade = { ...contagem.porUnidade };
  if (selecionada && porUnidade[selecionada] === undefined) {
    porUnidade[selecionada] = 0;
  }
  return Object.entries(porUnidade)
    .map(([id, n]) => ({ id, n, nome: nomeUnidade(id) }))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

function resumoContas(c: ContagemUsuarios): string {
  const partes = [
    `${c.total} ${c.total === 1 ? "conta" : "contas"}`,
    `${c.ativos} ${c.ativos === 1 ? "ativa" : "ativas"}`,
  ];
  if (c.pendentes > 0) {
    partes.push(`${c.pendentes} com acesso pendente`);
  }
  return partes.join(" · ");
}

/* ------------------------------------------------------------------ filtros */

function AbasStatus({
  status,
  contagem,
  onMudar,
}: {
  status: StatusFiltro;
  /** `null` enquanto a lista carrega: as abas aparecem sem número (zero seria mentira). */
  contagem: ContagemUsuarios | null;
  onMudar: (s: StatusFiltro) => void;
}) {
  const abas: { chave: StatusFiltro; rotulo: string; n: number | undefined }[] = [
    { chave: "todos", rotulo: "Todos", n: contagem?.total },
    { chave: "ativos", rotulo: "Ativos", n: contagem?.ativos },
    { chave: "inativos", rotulo: "Inativos", n: contagem?.inativos },
  ];
  // "Acesso pendente" só aparece quando existe alguém (ou já está selecionada).
  if ((contagem?.pendentes ?? 0) > 0 || status === "pendentes") {
    abas.push({ chave: "pendentes", rotulo: "Acesso pendente", n: contagem?.pendentes });
  }
  return (
    // biome-ignore lint/a11y/useSemanticElements: grupo de botões de alternância, não um <fieldset> de formulário
    <div className="us-abas" role="group" aria-label="Filtrar por status">
      {abas.map((a) => (
        <button
          key={a.chave}
          type="button"
          className={`us-aba${status === a.chave ? " on" : ""}`}
          aria-pressed={status === a.chave}
          onClick={() => onMudar(a.chave)}
        >
          {a.rotulo}
          {a.n !== undefined && <span className="us-aba-n">{a.n}</span>}
        </button>
      ))}
    </div>
  );
}

function BarraFiltros({
  filtros,
  contagem,
  refBusca,
  onFiltrar,
  onLimpar,
}: {
  filtros: FiltrosUsuarios;
  contagem: ContagemUsuarios;
  refBusca: React.RefObject<HTMLInputElement>;
  onFiltrar: (parcial: Partial<FiltrosUsuarios>) => void;
  onLimpar: () => void;
}) {
  const unidades = useMemo(
    () => unidadesDoFiltro(contagem, filtros.unidadeId),
    [contagem, filtros.unidadeId],
  );
  const ativos = contarFiltrosAtivos(filtros);

  return (
    <div className="us-barra">
      <div className="us-busca">
        <span className="us-busca-lupa">
          <Icon name="search" size={16} />
        </span>
        <input
          ref={refBusca}
          type="search"
          value={filtros.busca}
          placeholder="Buscar por nome, e-mail, perfil, eixo ou unidade…"
          aria-label="Buscar usuários"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => onFiltrar({ busca: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Escape" && filtros.busca) {
              e.stopPropagation();
              onFiltrar({ busca: "" });
            }
          }}
        />
        {filtros.busca ? (
          <button
            type="button"
            className="us-busca-limpar"
            aria-label="Limpar busca"
            onClick={() => {
              onFiltrar({ busca: "" });
              refBusca.current?.focus();
            }}
          >
            <Icon name="x" size={14} />
          </button>
        ) : (
          <span className="us-busca-atalho">/</span>
        )}
      </div>

      <FiltroPerfil
        selecionados={filtros.papeis}
        porPapel={contagem.porPapel}
        onMudar={(papeis) => onFiltrar({ papeis })}
      />

      <select
        className={`us-filtro us-select${filtros.eixo ? " on" : ""}`}
        aria-label="Filtrar por eixo"
        value={filtros.eixo}
        onChange={(e) => onFiltrar({ eixo: e.target.value })}
      >
        <option value="">Todos os eixos</option>
        {EIXOS.map((x) => (
          <option key={x} value={x}>
            {x} ({contagem.porEixo[x] ?? 0})
          </option>
        ))}
        <option value="sem">Sem eixo ({contagem.porEixo.sem ?? 0})</option>
      </select>

      {unidades.length > 0 && (
        <select
          className={`us-filtro us-select us-select-unidade${filtros.unidadeId ? " on" : ""}`}
          aria-label="Filtrar por unidade"
          value={filtros.unidadeId}
          onChange={(e) => onFiltrar({ unidadeId: e.target.value })}
        >
          <option value="">Todas as unidades</option>
          {unidades.map((u) => (
            <option key={u.id} value={u.id}>
              {u.nome} ({u.n})
            </option>
          ))}
        </select>
      )}

      {temFiltro(filtros) && (
        <button type="button" className="us-limpar" onClick={onLimpar}>
          Limpar filtros{ativos > 0 ? ` (${ativos})` : ""}
        </button>
      )}
    </div>
  );
}

/** Multi-seleção de perfis, com a contagem de contas em cada um. */
function FiltroPerfil({
  selecionados,
  porPapel,
  onMudar,
}: {
  selecionados: readonly string[];
  porPapel: Record<string, number>;
  onMudar: (papeis: string[]) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const idPainel = useId();
  const fechar = useCallback(() => setAberto(false), []);
  useFecharFora(raiz, aberto, fechar);

  const alternar = (p: string) =>
    onMudar(selecionados.includes(p) ? selecionados.filter((x) => x !== p) : [...selecionados, p]);
  const n = selecionados.length;

  return (
    <div
      className="us-pop-ancora"
      ref={raiz}
      onBlur={(e) => {
        // Tab para fora fecha — senão o painel ficava aberto cobrindo a tabela.
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setAberto(false);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && aberto) {
          e.stopPropagation();
          setAberto(false);
          botao.current?.focus();
        }
      }}
    >
      <button
        ref={botao}
        type="button"
        className={`us-filtro${n > 0 ? " on" : ""}`}
        // O cabeçalho da coluna também se chama "Perfil": o nome acessível distingue os dois.
        aria-label={n > 0 ? `Filtrar por perfil (${n} selecionados)` : "Filtrar por perfil"}
        aria-expanded={aberto}
        aria-controls={aberto ? idPainel : undefined}
        onClick={() => setAberto((a) => !a)}
      >
        <Icon name="filter" size={14} />
        Perfil
        {n > 0 && <span className="us-filtro-n">{n}</span>}
        <Icon name="chevronDown" size={14} />
      </button>
      {aberto && (
        <div
          className="us-pop"
          id={idPainel}
          // biome-ignore lint/a11y/useSemanticElements: painel flutuante com caixas de seleção, não um <fieldset> em fluxo
          role="group"
          aria-label="Filtrar por perfil"
          // Focável por script: clicar numa área "morta" do painel mantém o foco
          // dentro dele em vez de disparar o onBlur que o fecharia.
          tabIndex={-1}
        >
          {GRUPOS.map((g) => (
            <div key={g}>
              <div className="us-pop-grupo">{GRUPO_ROTULO[g]}</div>
              {PAPEIS_ORDEM.filter((p) => PAPEL_GRUPO[p] === g).map((p) => (
                <label key={p} className="us-pop-item">
                  <input type="checkbox" checked={selecionados.includes(p)} onChange={() => alternar(p)} />
                  <span>{rotuloPapel(p)}</span>
                  <span className="us-pop-n">{porPapel[p] ?? 0}</span>
                </label>
              ))}
            </div>
          ))}
          <div className="us-pop-rodape">
            <button type="button" className="us-limpar" disabled={n === 0} onClick={() => onMudar([])}>
              Limpar seleção
            </button>
            <button type="button" className="us-limpar forte" onClick={fechar}>
              Pronto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- tabela */

const COLUNAS: { coluna: ColunaOrdem; rotulo: string; classe: string }[] = [
  { coluna: "nome", rotulo: "Usuário", classe: "us-c-usuario" },
  { coluna: "papel", rotulo: "Perfil", classe: "us-c-perfil" },
  { coluna: "eixo", rotulo: "Eixo", classe: "us-c-eixo" },
  { coluna: "status", rotulo: "Status", classe: "us-c-status" },
];

function TabelaUsuarios({
  linhas,
  carregando,
  busca,
  ordem,
  euId,
  vazio,
  onOrdenar,
  onAbrir,
  onReativar,
}: {
  linhas: readonly UsuarioPublico[];
  carregando: boolean;
  busca: string;
  ordem: Ordem;
  euId?: string;
  vazio: React.ReactNode;
  onOrdenar: (c: ColunaOrdem) => void;
  onAbrir: (d: Dialogo) => void;
  onReativar: (u: UsuarioPublico) => void;
}) {
  if (!carregando && linhas.length === 0) {
    return <>{vazio}</>;
  }
  return (
    <table className="us-tabela" aria-busy={carregando}>
      <thead>
        <tr>
          {COLUNAS.map((c) => {
            const ativa = ordem.coluna === c.coluna;
            return (
              <th
                key={c.coluna}
                scope="col"
                className={c.classe}
                aria-sort={ativa ? (ordem.dir === "asc" ? "ascending" : "descending") : "none"}
              >
                <button
                  type="button"
                  className={`us-th-btn${ativa ? " on" : ""}`}
                  title={`Ordenar por ${c.rotulo.toLowerCase()}`}
                  onClick={() => onOrdenar(c.coluna)}
                >
                  {c.rotulo}
                  <Icon name={ativa && ordem.dir === "desc" ? "chevronDown" : "chevronUp"} size={13} />
                </button>
              </th>
            );
          })}
          <th scope="col" className="us-c-acoes">
            <span className="us-so-leitor">Ações</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {carregando
          ? ESQUELETO.map((larguras, i) => (
              <LinhaEsqueleto key={larguras.join("-")} larguras={larguras} n={i} />
            ))
          : linhas.map((u) => (
              <LinhaUsuario
                key={u.id}
                u={u}
                busca={busca}
                ehEu={u.id === euId}
                onAbrir={onAbrir}
                onReativar={onReativar}
              />
            ))}
      </tbody>
    </table>
  );
}

/** Larguras (%) de nome/e-mail de cada linha-esqueleto — variadas para não parecer grade. */
const ESQUELETO = [
  [46, 62],
  [38, 54],
  [52, 66],
  [34, 58],
  [44, 50],
  [40, 64],
];

function LinhaEsqueleto({ larguras, n }: { larguras: number[]; n: number }) {
  return (
    <tr className="us-linha-esq">
      <td className="us-c-usuario">
        <div className="us-pessoa">
          <span className="us-esq us-esq-avatar" />
          <div className="us-pessoa-txt">
            <span className="us-esq" style={{ width: `${larguras[0]}%` }} />
            <span className="us-esq" style={{ width: `${larguras[1]}%`, marginTop: 8 }} />
          </div>
        </div>
      </td>
      <td className="us-c-perfil">
        <span className="us-esq us-esq-pilula" style={{ width: 96 + (n % 3) * 18 }} />
      </td>
      <td className="us-c-eixo">
        <span className="us-esq us-esq-pilula" style={{ width: 40 }} />
      </td>
      <td className="us-c-status">
        <span className="us-esq us-esq-pilula" style={{ width: 62 }} />
      </td>
      <td className="us-c-acoes" />
    </tr>
  );
}

function LinhaUsuario({
  u,
  busca,
  ehEu,
  onAbrir,
  onReativar,
}: {
  u: UsuarioPublico;
  busca: string;
  ehEu: boolean;
  onAbrir: (d: Dialogo) => void;
  onReativar: (u: UsuarioPublico) => void;
}) {
  const vinculo =
    u.papel === "unidade"
      ? [nomeCargo(u.cargoUnidade), u.unidadeId ? nomeUnidade(u.unidadeId) : ""].filter(Boolean).join(" · ")
      : "";
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: atalho de mouse; pelo teclado a edição é o botão "Editar" da própria linha
    <tr
      className={`us-linha${u.ativo ? "" : " inativa"}`}
      onClick={() => {
        // Quem arrastou para selecionar (copiar o e-mail) não quer abrir a edição.
        if (!window.getSelection()?.toString()) {
          onAbrir({ tipo: "editar", usuario: u });
        }
      }}
    >
      <td className="us-c-usuario">
        <div className="us-pessoa">
          <Avatar nome={u.nome} id={u.id} inativo={!u.ativo} />
          <div className="us-pessoa-txt">
            <div className="us-nome">
              <span className="us-corta" title={u.nome}>
                <Destaque texto={u.nome} busca={busca} />
              </span>
              {ehEu && <span className="us-voce">Você</span>}
            </div>
            <div className="us-email us-corta" title={u.matricula}>
              <Destaque texto={u.matricula} busca={busca} />
            </div>
          </div>
        </div>
      </td>
      <td className="us-c-perfil">
        <PerfilBadge papel={u.papel} />
        {/* Abaixo de ~760px a coluna Eixo some; o chip passa a morar aqui. */}
        {u.eixo && (
          <span className="us-eixo-junto">
            <EixoChip eixo={u.eixo} />
          </span>
        )}
        {vinculo && (
          <div className="us-vinculo us-corta" title={vinculo}>
            <Destaque texto={vinculo} busca={busca} />
          </div>
        )}
      </td>
      <td className="us-c-eixo">
        <EixoChip eixo={u.eixo} />
      </td>
      <td className="us-c-status">
        <StatusPill status={statusUsuario(u)} />
      </td>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: só impede que o clique nos botões abra a edição da linha */}
      <td className="us-c-acoes" onClick={(e) => e.stopPropagation()}>
        <div className="us-acoes">
          <button
            type="button"
            className="us-btn-linha"
            aria-label={`Editar ${u.nome}`}
            onClick={() => onAbrir({ tipo: "editar", usuario: u })}
          >
            <Icon name="edit" size={14} />
            <span>Editar</span>
          </button>
          <MenuAcoes u={u} ehEu={ehEu} onAbrir={onAbrir} onReativar={onReativar} />
        </div>
      </td>
    </tr>
  );
}

/** Menu "⋯" da linha: as ações menos frequentes saem da frente, mas a 1 clique. */
function MenuAcoes({
  u,
  ehEu,
  onAbrir,
  onReativar,
}: {
  u: UsuarioPublico;
  ehEu: boolean;
  onAbrir: (d: Dialogo) => void;
  onReativar: (u: UsuarioPublico) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [paraCima, setParaCima] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const idMenu = useId();
  const fechar = useCallback(() => setAberto(false), []);
  useFecharFora(raiz, aberto, fechar);

  useEffect(() => {
    if (aberto) {
      raiz.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
    }
  }, [aberto]);

  const alternar = () => {
    // Perto do fim da janela o menu abre para cima — senão ficaria fora da tela.
    const r = botao.current?.getBoundingClientRect();
    setParaCima(!!r && r.bottom > 0 && window.innerHeight - r.bottom < 200);
    setAberto((a) => !a);
  };

  const navegar = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      setAberto(false);
      botao.current?.focus();
      return;
    }
    if (e.key === "Tab") {
      setAberto(false);
      return;
    }
    const passo: Record<string, number> = { ArrowDown: 1, ArrowUp: -1 };
    if (!(e.key in passo) && e.key !== "Home" && e.key !== "End") {
      return;
    }
    e.preventDefault();
    const itens = [
      ...(raiz.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []),
    ];
    const atual = itens.indexOf(document.activeElement as HTMLElement);
    const alvo =
      e.key === "Home"
        ? 0
        : e.key === "End"
          ? itens.length - 1
          : (atual + (passo[e.key] ?? 0) + itens.length) % itens.length;
    itens[alvo]?.focus();
  };

  /** Devolve o foco ao "⋯" antes de agir: é para lá que o foco volta ao fechar o diálogo. */
  const escolher = (acao: () => void) => {
    setAberto(false);
    botao.current?.focus();
    acao();
  };

  return (
    <div className="us-menu-ancora" ref={raiz}>
      <button
        ref={botao}
        type="button"
        className="us-btn-icone"
        aria-label={`Mais ações de ${u.nome}`}
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? idMenu : undefined}
        onClick={alternar}
      >
        <Icon name="dots" size={16} />
      </button>
      {aberto && (
        <div className={`us-menu${paraCima ? " cima" : ""}`} id={idMenu} role="menu" onKeyDown={navegar}>
          <button
            type="button"
            role="menuitem"
            className="us-menu-item"
            onClick={() => escolher(() => onAbrir({ tipo: "mensagem", usuario: u }))}
          >
            <Icon name="mail" size={15} />
            Mensagem de acesso
          </button>
          <button
            type="button"
            role="menuitem"
            className="us-menu-item"
            onClick={() => escolher(() => onAbrir({ tipo: "senha", usuario: u }))}
          >
            <Icon name="key" size={15} />
            Redefinir senha
          </button>
          <div className="us-menu-sep" />
          {u.ativo ? (
            <>
              <button
                type="button"
                role="menuitem"
                className="us-menu-item perigo"
                disabled={ehEu}
                onClick={() => escolher(() => onAbrir({ tipo: "desativar", usuario: u }))}
              >
                <Icon name="power" size={15} />
                Desativar conta
              </button>
              {ehEu && <div className="us-menu-nota">Você não pode desativar a própria conta.</div>}
            </>
          ) : (
            <button
              type="button"
              role="menuitem"
              className="us-menu-item"
              onClick={() => escolher(() => onReativar(u))}
            >
              <Icon name="userCheck" size={15} />
              Reativar conta
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Rodape({
  pg,
  totalGeral,
  tamanho,
  onPagina,
  onTamanho,
}: {
  pg: Pagina<UsuarioPublico>;
  totalGeral: number;
  tamanho: number;
  onPagina: (p: number) => void;
  onTamanho: (t: number) => void;
}) {
  const filtrado = pg.total !== totalGeral;
  return (
    <div className="us-rodape">
      <div aria-live="polite">
        Mostrando{" "}
        <b>
          {pg.inicio}–{pg.fim}
        </b>{" "}
        de <b>{pg.total}</b> {pg.total === 1 ? "usuário" : "usuários"}
        {filtrado && <span className="us-rodape-total"> · filtrado de {totalGeral}</span>}
      </div>
      {/* Com tudo cabendo na menor página não há o que paginar. */}
      {pg.total > (TAMANHOS_PAGINA[0] ?? 25) && (
        <div className="us-pager">
          <label className="us-pager-tam">
            Linhas por página
            <select
              className="us-filtro us-select"
              value={tamanho}
              onChange={(e) => onTamanho(Number(e.target.value))}
            >
              {TAMANHOS_PAGINA.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="us-btn-linha"
            disabled={pg.pagina <= 1}
            onClick={() => onPagina(pg.pagina - 1)}
          >
            <Icon name="back" size={14} />
            <span>Anterior</span>
          </button>
          <span className="us-pager-pos">
            Página {pg.pagina} de {pg.totalPaginas}
          </span>
          <button
            type="button"
            className="us-btn-linha"
            disabled={pg.pagina >= pg.totalPaginas}
            onClick={() => onPagina(pg.pagina + 1)}
          >
            <span>Próxima</span>
            <Icon name="chevron" size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function Estado({
  icone,
  titulo,
  texto,
  acao,
}: {
  icone: "alert" | "search" | "people";
  titulo: string;
  texto: string;
  acao?: React.ReactNode;
}) {
  return (
    <div className="us-estado">
      <span className="us-estado-ic">
        <Icon name={icone} size={20} />
      </span>
      <b>{titulo}</b>
      <p>{texto}</p>
      {acao}
    </div>
  );
}

/* ------------------------------------------------------------------- modais */

function FormModal({
  api,
  editando,
  euId,
  onClose,
  onSalvo,
}: {
  api: UsuariosApi;
  editando?: UsuarioPublico;
  euId?: string;
  onClose: () => void;
  onSalvo: (nome: string) => void;
}) {
  const f = useUsuarioForm({ api, editando, onSalvo, onFechar: onClose });
  // Com a requisição em andamento o diálogo não fecha: a resposta chegaria a um
  // diálogo que não existe mais e, na criação, a credencial nunca apareceria.
  const fecharSeLivre = () => {
    if (!f.salvando) {
      onClose();
    }
  };

  if (f.criado) {
    return (
      <Modal
        key="credencial"
        titulo="Usuário criado"
        sub={f.criado.nome}
        onClose={onClose}
        // A senha só aparece aqui: um clique distraído no fundo não pode levá-la embora.
        fecharNoFundo={false}
        foot={
          <div className="us-modal-foot">
            <button type="button" className="m-btn primary" onClick={onClose}>
              Concluir
            </button>
          </div>
        }
      >
        <CredencialPronta {...f.criado} />
      </Modal>
    );
  }

  return (
    <Modal
      key="formulario"
      titulo={f.ehEdicao ? "Editar usuário" : "Novo usuário"}
      sub={editando ? subtituloEdicao(editando) : "A pessoa recebe o acesso por mensagem."}
      largura={600}
      // Com algo digitado, clicar fora não descarta o formulário.
      fecharNoFundo={!f.sujo}
      onClose={fecharSeLivre}
      foot={
        <div className="us-modal-foot">
          <button type="button" className="m-btn" disabled={f.salvando} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="m-btn primary"
            disabled={f.salvando}
            onClick={() => void f.salvar()}
          >
            {f.salvando ? "Salvando…" : f.ehEdicao ? "Salvar alterações" : "Criar usuário"}
          </button>
        </div>
      }
    >
      <form
        // A validação é a do formulário (`faltasUsuario`), não a nativa: o
        // `type="email"` barraria no Enter um login antigo que não é e-mail.
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!f.salvando) {
            void f.salvar();
          }
        }}
      >
        <UsuarioCampos f={f} idPrefixo="nu" proprioUsuario={!!editando && editando.id === euId} />
        {/* Enter em qualquer campo salva (o botão visível mora no rodapé, fora do <form>). */}
        <button type="submit" hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}

/** "ana@… · conta criada em 12/03/2026" — o e-mail ATUAL fica à vista enquanto se edita. */
function subtituloEdicao(u: UsuarioPublico): string {
  const data = criadaEm(u);
  return data ? `${u.matricula} · conta criada em ${data}` : u.matricula;
}

function MensagemModal({
  usuario,
  api,
  onClose,
  onNovaSenha,
}: {
  usuario: UsuarioPublico;
  api: UsuariosApi;
  onClose: () => void;
  onNovaSenha: () => void;
}) {
  // Senha provisória do usuário: `undefined` = ainda buscando,
  // `null` = não existe mais (a pessoa já entrou ou trocou a senha).
  const [senha, setSenha] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let vivo = true;
    setSenha(undefined);
    // try/catch em vez de `.catch`: um throw SÍNCRONO não seria pego pela
    // cadeia de promessa e deixaria a tela presa em "Buscando a senha…".
    void (async () => {
      try {
        const r = await api.senhaProvisoria(usuario.id);
        if (vivo) {
          setSenha(r.senha);
        }
      } catch {
        // Cai na mensagem SEM senha — o mesmo que se mostra quando ela não existe.
        if (vivo) {
          setSenha(null);
        }
      }
    })();
    return () => {
      vivo = false;
    };
  }, [usuario.id, api]);

  return (
    <Modal
      titulo="Mensagem de acesso"
      sub={usuario.nome}
      onClose={onClose}
      foot={
        <div className="us-modal-foot">
          {/* Atalho para o caso real: a pessoa esqueceu a senha. Gera uma nova e
              cai no fluxo que produz a mensagem COMPLETA. */}
          <button type="button" className="m-btn" onClick={onNovaSenha}>
            Gerar nova senha
          </button>
          <button type="button" className="m-btn primary" onClick={onClose}>
            Fechar
          </button>
        </div>
      }
    >
      <CredencialPronta
        nome={usuario.nome}
        matricula={usuario.matricula}
        senha={senha ?? undefined}
        carregandoSenha={senha === undefined}
        papel={usuario.papel}
        unidadeNome={usuario.papel === "unidade" ? nomeUnidade(usuario.unidadeId ?? undefined) : undefined}
      />
    </Modal>
  );
}

function SenhaModal({
  usuario,
  api,
  onClose,
  onPronto,
}: { usuario: UsuarioPublico; api: UsuariosApi; onClose: () => void; onPronto: () => void }) {
  const [senha, setSenha] = useState(() => gerarSenhaProvisoria());
  const [redefinida, setRedefinida] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      await api.redefinirSenha(usuario.id, senha);
      // Não fecha: como na criação, esta é a única chance de ler a senha.
      setRedefinida(true);
      setSalvando(false);
      onPronto();
    } catch (e) {
      setErro(mensagemAmigavel(e));
      setSalvando(false);
    }
  };

  if (redefinida) {
    return (
      <Modal
        key="credencial"
        titulo="Senha redefinida"
        sub={usuario.nome}
        onClose={onClose}
        fecharNoFundo={false}
        foot={
          <div className="us-modal-foot">
            <button type="button" className="m-btn primary" onClick={onClose}>
              Concluir
            </button>
          </div>
        }
      >
        <CredencialPronta
          nome={usuario.nome}
          matricula={usuario.matricula}
          senha={senha}
          papel={usuario.papel}
          unidadeNome={usuario.papel === "unidade" ? nomeUnidade(usuario.unidadeId ?? undefined) : undefined}
          redefinicao
        />
      </Modal>
    );
  }

  const curta = senha.length < 8;
  return (
    <Modal
      key="formulario"
      titulo="Redefinir senha"
      sub={usuario.nome}
      // Salvando: não fecha, senão a senha nova é gravada e a credencial nunca aparece.
      onClose={() => {
        if (!salvando) {
          onClose();
        }
      }}
      foot={
        <div className="us-modal-foot">
          <button type="button" className="m-btn" disabled={salvando} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="m-btn primary"
            disabled={salvando || curta}
            onClick={() => void salvar()}
          >
            {salvando ? "Salvando…" : "Definir nova senha"}
          </button>
        </div>
      }
    >
      <div className="uf">
        {erro && (
          <p className="uf-alerta" role="alert">
            <Icon name="alert" size={16} />
            <span>{erro}</span>
          </p>
        )}
        <div className="m-field">
          <label htmlFor="su-senha">Nova senha provisória (mín. 8)</label>
          <div className="uf-linha">
            <input
              id="su-senha"
              className={`m-input uf-mono${curta ? " missing" : ""}`}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={curta || undefined}
              aria-describedby="su-senha-dica"
            />
            <button type="button" className="m-btn uf-gerar" onClick={() => setSenha(gerarSenhaProvisoria())}>
              <Icon name="refresh" size={15} />
              Gerar
            </button>
          </div>
          <p className={curta ? "u-erro-campo" : "uf-dica"} id="su-senha-dica">
            {curta
              ? "A senha precisa ter no mínimo 8 caracteres."
              : "A senha atual deixa de funcionar. Em seguida você recebe a mensagem pronta para enviar."}
          </p>
        </div>
      </div>
    </Modal>
  );
}

function DesativarModal({
  usuario,
  onClose,
  onConfirmar,
}: { usuario: UsuarioPublico; onClose: () => void; onConfirmar: () => Promise<void> }) {
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const confirmar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      await onConfirmar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
      setSalvando(false);
    }
  };

  return (
    <Modal
      titulo="Desativar esta conta?"
      sub={usuario.matricula}
      onClose={() => {
        if (!salvando) {
          onClose();
        }
      }}
      foot={
        <div className="us-modal-foot">
          <button type="button" className="m-btn" disabled={salvando} onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="m-btn danger" disabled={salvando} onClick={() => void confirmar()}>
            {salvando ? "Desativando…" : "Desativar conta"}
          </button>
        </div>
      }
    >
      <div className="uf">
        {erro && (
          <p className="uf-alerta" role="alert">
            <Icon name="alert" size={16} />
            <span>{erro}</span>
          </p>
        )}
        <p className="us-confirma">
          <b>{usuario.nome}</b> não vai mais conseguir entrar no CAINT. Nada é apagado — o histórico da conta
          continua como está, e você pode reativá-la quando quiser.
        </p>
      </div>
    </Modal>
  );
}
